import { Reorder, useDragControls } from 'motion/react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FAVORITES } from '@pages/Trade/utils/constants'

import { useModal } from '@context/ModalsContext'

import { Truncate } from '@ui/Truncate/Truncate'
import { Body1, Body2 } from '@ui/Typography'
import { Button } from '@ui/components/Button/Button'
import { IconButton } from '@ui/components/Button/IconButton'
import { Modal } from '@ui/components/Modal'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { DragVerticalIcon } from '@ui/icons/DragVertical.icon'
import { EditIcon } from '@ui/icons/Edit.icon'
import { TrashIcon } from '@ui/icons/Trash.icon'

import { useWindowResize } from '@utils/domUtils'

import { WatchlistModel } from '@model/DxTrade.types'

import { Box } from '@shared/components/Box'

import { useBulkUpdateWatchlistOrderValues } from '../hooks/useBulkUpdateWatchlistOrderValues'
import { AddInstrumentsModal } from './Modals/AddInstrumentsModal'
import { CreateWatchlistModal } from './Modals/CreateWatchlistModal'
import { DeleteWatchlistModal } from './Modals/DeleteWatchlistModal'
import { EditWatchlistModal } from './Modals/EditWatchlistModal'
import { sortWatchlistsByOrder } from './helpers'

import styles from './ManageWatchlistsDropdown.module.scss'

interface ManageWatchlistsDropdownProps {
  ownerExternalId?: string
  accountCode?: string
  existingNames?: string[]
  favoritesWatchlist?: WatchlistModel
  nonFavoriteWatchlists?: WatchlistModel[]
  onCreated?: (name: string) => void
  onEdited?: (newName: string) => void
  onDeleted?: (name: string) => void
}

interface WatchlistDragItemProps {
  watchlist: WatchlistModel
  onDragEnd: () => void
  onEditClick: () => void
  onDeleteClick: () => void
  skipLayoutAnimation: boolean
}

const WatchlistDragItem: React.FC<WatchlistDragItemProps> = ({
  watchlist,
  onDragEnd,
  onEditClick,
  onDeleteClick,
  skipLayoutAnimation,
}) => {
  const controls = useDragControls()

  return (
    <Reorder.Item
      as='div'
      value={watchlist}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragEnd}
      initial={false}
      animate={{ opacity: 1 }}
      whileDrag={{ opacity: 0.4 }}
      transition={skipLayoutAnimation ? { layout: { duration: 0 } } : undefined}
    >
      <Box flex align='center' gap='x12' px='x12' py='x8' fullWidth>
        <span className={styles.dragHandle} onPointerDown={(e) => controls.start(e)}>
          <DragVerticalIcon />
        </span>
        <Box flex align='center' justify='space-between' fullWidth minWidth='0' gap='x4'>
          <Truncate>
            <Body2 medium>{watchlist.name}</Body2>
          </Truncate>
          <Box flex gap='x8'>
            <IconButton variant='secondary' size='sm' icon={EditIcon} onClick={onEditClick} />
            <IconButton variant='secondary' size='sm' icon={TrashIcon} onClick={onDeleteClick} />
          </Box>
        </Box>
      </Box>
    </Reorder.Item>
  )
}

interface WatchlistReorderListProps {
  watchlists: WatchlistModel[]
  onReorder: (reordered: WatchlistModel[]) => void
  onEditClick: (watchlist: WatchlistModel) => void
  onDeleteClick: (watchlist: WatchlistModel) => void
}

const WatchlistReorderList: React.FC<WatchlistReorderListProps> = ({
  watchlists,
  onReorder,
  onEditClick,
  onDeleteClick,
}) => {
  const [orderedWatchlists, setOrderedWatchlists] = useState(() =>
    sortWatchlistsByOrder(watchlists)
  )
  const orderedRef = useRef(orderedWatchlists)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true))
  }, [])

  useEffect(() => {
    const next = sortWatchlistsByOrder(watchlists)
    orderedRef.current = next
    setOrderedWatchlists(next)
  }, [watchlists])

  const handleDragReorder = (newOrder: WatchlistModel[]) => {
    orderedRef.current = newOrder
    setOrderedWatchlists(newOrder)
  }

  const handleDragEnd = () => onReorder(orderedRef.current)

  return (
    <Reorder.Group
      as='div'
      values={orderedWatchlists}
      onReorder={handleDragReorder}
      initial={false}
    >
      {orderedWatchlists.map((watchlist) => (
        <WatchlistDragItem
          key={watchlist.id}
          watchlist={watchlist}
          onDragEnd={handleDragEnd}
          onEditClick={() => onEditClick(watchlist)}
          onDeleteClick={() => onDeleteClick(watchlist)}
          skipLayoutAnimation={!mounted}
        />
      ))}
    </Reorder.Group>
  )
}

export const ManageWatchlistsDropdown: React.FC<ManageWatchlistsDropdownProps> = memo(
  ({
    ownerExternalId,
    accountCode,
    existingNames,
    favoritesWatchlist,
    nonFavoriteWatchlists = [],
    onCreated,
    onEdited,
    onDeleted,
  }) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const isMobile = useWindowResize()
    const createWatchlistModal = useModal(CreateWatchlistModal)
    const editWatchlistModal = useModal(EditWatchlistModal)
    const deleteWatchlistModal = useModal(DeleteWatchlistModal)
    const addInstrumentsModal = useModal(AddInstrumentsModal)
    const { mutate: bulkUpdateWatchlistOrderValues } = useBulkUpdateWatchlistOrderValues()

    const handleReorder = useCallback(
      (reordered: WatchlistModel[]) => {
        const orderValues = reordered.map((watchlist, i) => ({
          id: watchlist.id,
          orderValue: i + 1,
        }))
        bulkUpdateWatchlistOrderValues({ orderValues })
      },
      [bulkUpdateWatchlistOrderValues]
    )

    const handleEditClick = useCallback(
      (watchlist: WatchlistModel) => {
        setIsOpen(false)
        editWatchlistModal.open({
          watchlistName: watchlist.name ?? '',
          watchlistId: watchlist.id,
          accountCode,
          existingSymbols: watchlist.symbols ?? [],
          existingNames,
          onEdited,
        })
      },
      [editWatchlistModal, accountCode, existingNames, onEdited]
    )

    const handleDeleteClick = useCallback(
      (watchlist: WatchlistModel) => {
        setIsOpen(false)
        deleteWatchlistModal.open({
          watchlistId: watchlist.id,
          onDeleted: () => onDeleted?.(watchlist.name ?? ''),
        })
      },
      [deleteWatchlistModal, onDeleted]
    )

    return (
      <Popover
        direction='bottom'
        distance='0.5rem'
        label={t('Instruments.Manage watchlists')}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        modalFooter={(close) => (
          <>
            <Button.Primary
              fullWidth
              onClick={() => {
                setIsOpen(false)
                createWatchlistModal.open({ ownerExternalId, existingNames, onCreated })
              }}
            >
              {t('Instruments.Add new')}
            </Button.Primary>
            <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
          </>
        )}
        content={
          <div className={styles.card}>
            <PopoverCard>
              <Box px='x12' py='x8'>
                <Body1 medium>{t('Instruments.Manage watchlists')}</Body1>
              </Box>

              <PopoverList>
                <Box flex align='center' gap='x12' px='x12' py='x8' fullWidth>
                  <span className={styles.dragHandleDisabled}>
                    <DragVerticalIcon />
                  </span>
                  <Box flex align='center' justify='space-between' fullWidth>
                    <Body2 medium>{FAVORITES}</Body2>
                    <Box flex gap='x8'>
                      <IconButton
                        variant='secondary'
                        size='sm'
                        icon={EditIcon}
                        onClick={() => {
                          setIsOpen(false)
                          if (favoritesWatchlist) {
                            editWatchlistModal.open({
                              watchlistName: favoritesWatchlist.name ?? '',
                              watchlistId: favoritesWatchlist.id,
                              accountCode,
                              existingSymbols: favoritesWatchlist.symbols ?? [],
                              existingNames,
                              nameDisabled: true,
                              onEdited,
                            })
                          } else {
                            addInstrumentsModal.open({
                              watchlistName: FAVORITES,
                              accountCode,
                              ownerExternalId,
                              favoriteSymbols: [],
                            })
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <WatchlistReorderList
                  watchlists={nonFavoriteWatchlists}
                  onReorder={handleReorder}
                  onEditClick={handleEditClick}
                  onDeleteClick={handleDeleteClick}
                />
              </PopoverList>
              {!isMobile && (
                <Box p='x8'>
                  <Button.Primary
                    fullWidth
                    onClick={() => {
                      setIsOpen(false)
                      createWatchlistModal.open({ ownerExternalId, existingNames, onCreated })
                    }}
                  >
                    {t('Instruments.Add new')}
                  </Button.Primary>
                </Box>
              )}
            </PopoverCard>
          </div>
        }
      >
        <IconButton
          variant='secondary'
          size='sm'
          icon={EditIcon}
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen((prev) => !prev)
          }}
        />
      </Popover>
    )
  }
)
